import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useMerchant } from '../../contexts/MerchantContext';
import { useSubscription } from '../../contexts/SubscriptionContext';

/**
 * Guards paywall pages (trial-expired, subscription-suspended). Redirects the
 * user to the admin dashboard as soon as the subscription becomes active again
 * or subscription enforcement is turned off, so a manual trial extension is
 * picked up on the next navigation/refresh without requiring a fresh login.
 */
const PaywallRoute = ({ children }) => {
  const { isAuthenticated, hasMerchant, loading: authLoading } = useAuth();
  const { loading: merchantLoading } = useMerchant();
  const {
    subscription,
    loading: subLoading,
    isActive,
    enforcementEnabled,
  } = useSubscription();
  const location = useLocation();

  // Wait for all state (including feature flags, folded into subLoading) to
  // resolve before deciding. enforcementEnabled is briefly false while flags
  // load, which previously bounced paywall pages straight to /admin.
  if (authLoading || merchantLoading || subLoading) {
    return null;
  }

  if (!isAuthenticated()) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  if (!hasMerchant()) {
    return <Navigate to="/setup-merchant" replace />;
  }

  if (!enforcementEnabled) {
    return <Navigate to="/admin" replace />;
  }

  if (subscription && isActive()) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

export default PaywallRoute;
